import { createTheme, alpha } from '@mui/material/styles';

declare module '@mui/material/styles' {
  interface Palette {
    sidebar: {
      background: string;
      surface: string;
      border: string;
      text: string;
      textMuted: string;
      activeBackground: string;
      activeText: string;
      hoverBackground: string;
    };
    neutral: {
      50: string; 100: string; 200: string; 300: string; 400: string;
      500: string; 600: string; 700: string; 800: string; 900: string;
    };
  }
  interface PaletteOptions {
    sidebar?: Palette['sidebar'];
    neutral?: Palette['neutral'];
  }
}

// ── Design tokens ────────────────────────────────────────────────────────────
const BLUE_50  = '#EFF6FF';
const BLUE_100 = '#DBEAFE';
const BLUE_600 = '#2563EB';
const BLUE_700 = '#1D4ED8';
const BLUE_800 = '#1E40AF';

const SLATE_50  = '#F8FAFC';
const SLATE_100 = '#F1F5F9';
const SLATE_200 = '#E2E8F0';
const SLATE_300 = '#CBD5E1';
const SLATE_400 = '#94A3B8';
const SLATE_500 = '#64748B';
const SLATE_600 = '#475569';
const SLATE_700 = '#334155';
const SLATE_800 = '#1E293B';
const SLATE_900 = '#0F172A';
const SLATE_950 = '#020617';

export const theme = createTheme({
  palette: {
    mode: 'light',

    primary: {
      main: BLUE_600,
      dark: BLUE_700,
      light: '#60A5FA',
      contrastText: '#fff',
    },
    secondary: {
      main: '#0D9488',
      dark: '#0F766E',
      light: '#5EEAD4',
      contrastText: '#fff',
    },
    success: {
      main: '#059669',
      light: '#34D399',
      dark: '#047857',
      contrastText: '#fff',
    },
    warning: {
      main: '#D97706',
      light: '#FCD34D',
      dark: '#B45309',
      contrastText: '#fff',
    },
    error: {
      main: '#DC2626',
      light: '#FCA5A5',
      dark: '#B91C1C',
      contrastText: '#fff',
    },
    info: {
      main: '#0284C7',
      light: '#7DD3FC',
      dark: '#0369A1',
      contrastText: '#fff',
    },
    background: {
      default: '#EEF1F6',
      paper: '#FFFFFF',
    },
    text: {
      primary: SLATE_900,
      secondary: SLATE_500,
      disabled: SLATE_400,
    },
    divider: SLATE_200,

    neutral: {
      50: SLATE_50, 100: SLATE_100, 200: SLATE_200, 300: SLATE_300,
      400: SLATE_400, 500: SLATE_500, 600: SLATE_600, 700: SLATE_700,
      800: SLATE_800, 900: SLATE_900,
    },

    sidebar: {
      background: '#1B2433',
      surface: '#243044',
      border: 'rgba(255,255,255,0.08)',
      text: '#A8B3C5',
      textMuted: '#6B778C',
      activeBackground: alpha(BLUE_600, 0.18),
      activeText: '#FFFFFF',
      hoverBackground: alpha('#FFFFFF', 0.05),
    },
  },

  typography: {
    fontFamily: '"Inter", system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
    h1: { fontWeight: 800, fontSize: '2.25rem', letterSpacing: '-0.03em', lineHeight: 1.2 },
    h2: { fontWeight: 700, fontSize: '1.875rem', letterSpacing: '-0.025em', lineHeight: 1.25 },
    h3: { fontWeight: 700, fontSize: '1.5rem', letterSpacing: '-0.02em', lineHeight: 1.3 },
    h4: { fontWeight: 700, fontSize: '1.25rem', letterSpacing: '-0.015em', lineHeight: 1.35 },
    h5: { fontWeight: 700, fontSize: '1.125rem', letterSpacing: '-0.01em', lineHeight: 1.4 },
    h6: { fontWeight: 700, fontSize: '1rem', letterSpacing: '-0.005em', lineHeight: 1.45 },
    subtitle1: { fontWeight: 600, fontSize: '0.9375rem', lineHeight: 1.5 },
    subtitle2: { fontWeight: 600, fontSize: '0.875rem', lineHeight: 1.5 },
    body1: { fontSize: '0.9375rem', lineHeight: 1.7, color: SLATE_700 },
    body2: { fontSize: '0.875rem', lineHeight: 1.65, color: SLATE_600 },
    button: { fontWeight: 600, textTransform: 'none', fontSize: '0.875rem', letterSpacing: '0.005em' },
    caption: { fontSize: '0.75rem', lineHeight: 1.5, color: SLATE_500 },
    overline: { fontWeight: 700, fontSize: '0.6875rem', letterSpacing: '0.1em', textTransform: 'uppercase', color: SLATE_500 },
  },

  shape: { borderRadius: 12 },

  shadows: [
    'none',
    '0 1px 2px rgba(15,23,42,0.05)',
    '0 1px 3px rgba(15,23,42,0.07), 0 1px 2px rgba(15,23,42,0.04)',
    '0 2px 8px rgba(15,23,42,0.08), 0 1px 3px rgba(15,23,42,0.04)',
    '0 4px 12px rgba(15,23,42,0.09), 0 2px 4px rgba(15,23,42,0.04)',
    '0 6px 16px rgba(15,23,42,0.10), 0 2px 6px rgba(15,23,42,0.05)',
    '0 8px 20px rgba(15,23,42,0.11)',
    '0 10px 24px rgba(15,23,42,0.12)',
    '0 12px 28px rgba(15,23,42,0.13)',
    '0 14px 32px rgba(15,23,42,0.14)',
    '0 16px 36px rgba(15,23,42,0.15)',
    '0 18px 40px rgba(15,23,42,0.16)',
    '0 20px 44px rgba(15,23,42,0.17)',
    '0 22px 48px rgba(15,23,42,0.18)',
    '0 24px 52px rgba(15,23,42,0.19)',
    '0 26px 56px rgba(15,23,42,0.20)',
    '0 28px 60px rgba(15,23,42,0.21)',
    '0 30px 64px rgba(15,23,42,0.22)',
    '0 32px 68px rgba(15,23,42,0.23)',
    '0 34px 72px rgba(15,23,42,0.24)',
    '0 36px 76px rgba(15,23,42,0.25)',
    '0 38px 80px rgba(15,23,42,0.26)',
    '0 40px 84px rgba(15,23,42,0.27)',
    '0 42px 88px rgba(15,23,42,0.28)',
    '0 44px 92px rgba(15,23,42,0.29)',
  ],

  components: {
    // ── Buttons ────────────────────────────────────────────────────────────
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: {
          borderRadius: 10,
          padding: '9px 20px',
          fontSize: '0.875rem',
          fontWeight: 600,
          letterSpacing: '0.005em',
          transition: 'all 0.15s ease',
          '&:active': { transform: 'scale(0.98)' },
        },
        contained: {
          boxShadow: '0 1px 2px rgba(0,0,0,0.08)',
          '&:hover': { boxShadow: '0 4px 12px rgba(37,99,235,0.3)', transform: 'translateY(-1px)' },
          '&:active': { transform: 'translateY(0)', boxShadow: 'none' },
        },
        containedPrimary: {
          background: `linear-gradient(135deg, ${BLUE_600} 0%, ${BLUE_800} 100%)`,
          '&:hover': { background: `linear-gradient(135deg, ${BLUE_700} 0%, ${BLUE_800} 100%)` },
        },
        outlined: {
          borderWidth: '1.5px',
          '&:hover': { borderWidth: '1.5px', backgroundColor: alpha(BLUE_600, 0.04) },
        },
        text: {
          '&:hover': { backgroundColor: alpha(BLUE_600, 0.06) },
        },
        sizeSmall: { padding: '5px 14px', fontSize: '0.8125rem', borderRadius: 8 },
        sizeLarge: { padding: '12px 28px', fontSize: '0.9375rem', borderRadius: 12 },
      },
    },

    MuiIconButton: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          transition: 'all 0.15s ease',
          '&:hover': { backgroundColor: alpha(SLATE_900, 0.06) },
        },
        sizeSmall: { borderRadius: 8 },
      },
    },

    // ── Cards ──────────────────────────────────────────────────────────────
    MuiCard: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: {
          borderRadius: 16,
          border: `1px solid ${SLATE_200}`,
          boxShadow: '0 1px 2px rgba(27,36,51,0.04)',
          overflow: 'hidden',
          transition: 'box-shadow 0.2s ease, transform 0.2s ease',
          '&:hover': {
            boxShadow: '0 6px 20px rgba(15,23,42,0.10)',
          },
        },
      },
    },

    MuiCardContent: {
      styleOverrides: {
        root: {
          padding: '24px',
          '&:last-child': { paddingBottom: '24px' },
        },
      },
    },

    // ── Paper ──────────────────────────────────────────────────────────────
    MuiPaper: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: { backgroundImage: 'none' },
        outlined: { borderColor: SLATE_200 },
        elevation1: { boxShadow: '0 1px 4px rgba(15,23,42,0.06)' },
        elevation2: { boxShadow: '0 2px 8px rgba(15,23,42,0.08)' },
        elevation3: { boxShadow: '0 4px 16px rgba(15,23,42,0.10)' },
        elevation4: { boxShadow: '0 8px 24px rgba(15,23,42,0.12)' },
      },
    },

    // ── Inputs ─────────────────────────────────────────────────────────────
    MuiTextField: {
      defaultProps: { variant: 'outlined', size: 'small' },
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 10,
            backgroundColor: '#fff',
            fontSize: '0.875rem',
            fontWeight: 500,
            '& fieldset': { borderColor: SLATE_200, borderWidth: '1px' },
            '&:hover fieldset': { borderColor: SLATE_300 },
            '&.Mui-focused fieldset': { borderColor: BLUE_600, borderWidth: '1.5px' },
            '&.Mui-focused': { backgroundColor: '#fff' },
          },
          '& .MuiInputLabel-root': { fontSize: '0.875rem', fontWeight: 500, color: SLATE_500 },
          '& .MuiInputLabel-root.Mui-focused': { color: BLUE_600 },
        },
      },
    },

    MuiSelect: {
      styleOverrides: {
        root: { borderRadius: 10, fontSize: '0.875rem' },
      },
    },

    // ── Table ──────────────────────────────────────────────────────────────
    MuiTableHead: {
      styleOverrides: {
        root: {
          '& .MuiTableCell-root': {
            backgroundColor: SLATE_50,
            fontWeight: 700,
            fontSize: '0.7rem',
            color: SLATE_500,
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            borderBottom: `1px solid ${SLATE_200}`,
            padding: '12px 16px',
            whiteSpace: 'nowrap',
          },
        },
      },
    },

    MuiTableRow: {
      styleOverrides: {
        root: {
          '&.MuiTableRow-hover:hover': { backgroundColor: BLUE_50 },
          '&:last-child td': { borderBottom: 0 },
        },
      },
    },

    MuiTableCell: {
      styleOverrides: {
        root: {
          borderColor: SLATE_100,
          padding: '14px 16px',
          fontSize: '0.875rem',
          color: SLATE_700,
        },
      },
    },

    MuiTablePagination: {
      styleOverrides: {
        root: { borderTop: `1px solid ${SLATE_100}`, color: SLATE_500, fontSize: '0.8125rem' },
        selectLabel: { fontSize: '0.8125rem', fontWeight: 500 },
        displayedRows: { fontSize: '0.8125rem', fontWeight: 500 },
      },
    },

    // ── Chips ──────────────────────────────────────────────────────────────
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          fontWeight: 600,
          fontSize: '0.75rem',
          height: 26,
          letterSpacing: '0.01em',
        },
        sizeSmall: { height: 22, fontSize: '0.7rem', borderRadius: 6 },
        colorSuccess: {
          backgroundColor: '#EAF7F0',
          color: '#1B6B45',
          '& .MuiChip-icon': { color: '#1B6B45' },
        },
        colorError: {
          backgroundColor: '#F8EDED',
          color: '#9B2C2C',
          '& .MuiChip-icon': { color: '#9B2C2C' },
        },
        colorWarning: {
          backgroundColor: '#F8F1E4',
          color: '#8A5A12',
          '& .MuiChip-icon': { color: '#8A5A12' },
        },
        colorInfo: {
          backgroundColor: '#E8EEF8',
          color: '#1E4BB8',
          '& .MuiChip-icon': { color: '#1E4BB8' },
        },
        colorPrimary: {
          backgroundColor: '#E8EEF8',
          color: BLUE_700,
        },
      },
    },

    // ── Dialogs ────────────────────────────────────────────────────────────
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: 20,
          boxShadow: '0 25px 60px rgba(15,23,42,0.24), 0 8px 20px rgba(15,23,42,0.10)',
        },
        paperWidthSm: { maxWidth: 480 },
      },
    },

    MuiDialogTitle: {
      styleOverrides: {
        root: {
          fontWeight: 700,
          fontSize: '1.0625rem',
          padding: '28px 28px 12px',
          color: SLATE_900,
        },
      },
    },

    MuiDialogContent: {
      styleOverrides: {
        root: { padding: '8px 28px 16px' },
      },
    },

    MuiDialogActions: {
      styleOverrides: {
        root: {
          padding: '8px 28px 24px',
          gap: 8,
          '& .MuiButton-root': { minWidth: 90 },
        },
      },
    },

    // ── App bar ────────────────────────────────────────────────────────────
    MuiAppBar: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: { backgroundImage: 'none' },
      },
    },

    // ── Tooltip ────────────────────────────────────────────────────────────
    MuiTooltip: {
      defaultProps: { arrow: true },
      styleOverrides: {
        tooltip: {
          borderRadius: 8,
          fontSize: '0.75rem',
          fontWeight: 500,
          backgroundColor: SLATE_900,
          padding: '6px 12px',
          maxWidth: 220,
        },
        arrow: { color: SLATE_900 },
      },
    },

    // ── Misc ───────────────────────────────────────────────────────────────
    MuiAlert: {
      styleOverrides: {
        root: { borderRadius: 12, fontSize: '0.875rem', fontWeight: 500 },
        standardSuccess: { backgroundColor: '#F0FDF4', color: '#15803D', border: '1px solid #BBF7D0' },
        standardError:   { backgroundColor: '#FEF2F2', color: '#B91C1C', border: '1px solid #FECACA' },
        standardWarning: { backgroundColor: '#FFFBEB', color: '#B45309', border: '1px solid #FDE68A' },
        standardInfo:    { backgroundColor: '#EFF6FF', color: '#1D4ED8', border: '1px solid #BFDBFE' },
      },
    },

    MuiDivider: {
      styleOverrides: { root: { borderColor: SLATE_100 } },
    },

    MuiAvatar: {
      styleOverrides: {
        root: { fontWeight: 700, fontSize: '0.875rem' },
        colorDefault: { backgroundColor: '#E8EEF8', color: '#1D4ED8' },
      },
    },

    MuiSwitch: {
      styleOverrides: {
        root: { padding: 6 },
        track: { borderRadius: 12, opacity: 1, backgroundColor: SLATE_300 },
        switchBase: {
          '&.Mui-checked + .MuiSwitch-track': { opacity: 1, backgroundColor: BLUE_600 },
        },
        thumb: { width: 16, height: 16, boxShadow: '0 1px 3px rgba(0,0,0,0.15)' },
      },
    },

    MuiListItemButton: {
      styleOverrides: {
        root: { borderRadius: 10, margin: '1px 8px', padding: '8px 12px', fontSize: '0.875rem' },
      },
    },

    MuiSkeleton: {
      styleOverrides: {
        root: { borderRadius: 8, backgroundColor: SLATE_100 },
      },
    },

    MuiBadge: {
      styleOverrides: {
        badge: { fontWeight: 700, fontSize: '0.65rem', minWidth: 18, height: 18, borderRadius: 9 },
      },
    },

    MuiLinearProgress: {
      styleOverrides: {
        root: { borderRadius: 6, height: 6, backgroundColor: SLATE_100 },
        bar: { borderRadius: 6 },
      },
    },

    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 600,
          fontSize: '0.875rem',
          minHeight: 44,
          color: SLATE_500,
          '&.Mui-selected': { color: BLUE_600 },
        },
      },
    },

    MuiTabs: {
      styleOverrides: {
        indicator: { height: 2.5, borderRadius: 2 },
      },
    },
  },
});

// ── Design tokens export ──────────────────────────────────────────────────────
export const tokens = {
  blue: { 50: BLUE_50, 100: BLUE_100, 600: BLUE_600, 700: BLUE_700, 800: BLUE_800 },
  slate: {
    50: SLATE_50, 100: SLATE_100, 200: SLATE_200, 300: SLATE_300,
    400: SLATE_400, 500: SLATE_500, 600: SLATE_600, 700: SLATE_700,
    800: SLATE_800, 900: SLATE_900, 950: SLATE_950,
  },
  status: {
    success: { main: '#059669', bg: '#EAF7F0', text: '#1B6B45', dot: '#2F9E6A' },
    warning: { main: '#D97706', bg: '#F8F1E4', text: '#8A5A12', dot: '#C9892A' },
    danger:  { main: '#DC2626', bg: '#F8EDED', text: '#9B2C2C', dot: '#D64545' },
    info:    { main: '#0284C7', bg: '#EAF0FB', text: '#1E4BB8', dot: '#2F6FED' },
    neutral: { main: '#64748B', bg: '#F1F3F6', text: '#5B6575', dot: '#8A94A6' },
  },
  spacing: { xs: 4, sm: 8, md: 12, lg: 16, xl: 24, '2xl': 32, '3xl': 48 },
  radius: { sm: 8, md: 12, lg: 16, xl: 20, '2xl': 24 },
  shadow: {
    card: '0 1px 4px rgba(15,23,42,0.06)',
    md:   '0 4px 16px rgba(15,23,42,0.10)',
    lg:   '0 12px 36px rgba(15,23,42,0.14)',
    xl:   '0 24px 60px rgba(15,23,42,0.20)',
  },
  motion: {
    fast: '150ms ease',
    normal: '200ms ease',
    slow: '300ms ease',
  },
};

/** Dark palette scaffold — ready for ThemeMode toggle without hardcoding in components */
export const darkPalette = {
  mode: 'dark' as const,
  primary: { main: '#3B82F6', dark: '#2563EB', light: '#93C5FD', contrastText: '#fff' },
  background: { default: '#0B1220', paper: '#151E2E' },
  text: { primary: '#F1F5F9', secondary: '#94A3B8', disabled: '#64748B' },
  divider: 'rgba(148,163,184,0.16)',
};
